import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Controller } from "react-hook-form";
import { ContentPageEditProps } from "..";
import Form from "../../Form";
import FormFieldErrors from "../../Form/FormFieldErrors";
import { TbEdit, TbHandMove, TbTrash } from "react-icons/tb";
import { toast } from "react-toastify";
import { FeatureType } from "../../../types/api";
import { useSiteState } from "../../../utils/useSiteState";
import { imagePath } from "../../../utils/urls";

const reorder = (
  list: any,
  startIndex: number,
  endIndex: number
): FeatureType[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result as FeatureType[];
};

const grid = 8;

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  userSelect: "none",
  padding: grid * 2,
  margin: `0 0 10px 0`,
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: isDragging ? "#38343e" : "#110F15",
  ...draggableStyle,
});

const getListStyle = (isDraggingOver: boolean) => ({
  background: "transparent",
  padding: grid,
  width: "100%",
  borderRadius: 14,
});

const ContentPageEditFeatures: React.FC<ContentPageEditProps> = ({ page }) => {
  const { site } = useSiteState();

  const getDefaults = () => {
    const { page_template_config } = page;

    return {
      page_template_config,
    };
  };

  const onSubmit = async (responseData: any) => {
    // await fetchUser();

    toast("Page content updated", { type: "success" });
  };

  return (
    <Form
      getDefaults={getDefaults}
      onCompleted={onSubmit}
      buttonTitle="Update Content"
      endpoint={`sites/${site.id}/pages/${page.id}`}
      method="put"
      className="w-full text-white"
      render={({ errors, control }) => (
        <>
          <div className="mb-4 mt-6 ">
            <label className="mb-2 block ml-4 text-sm font-medium">
              Features
            </label>
            <Controller
              name="page_template_config.features"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }: any): JSX.Element => (
                <DragDropContext
                  onDragEnd={async (result: any) => {
                    if (!result.destination) {
                      return;
                    }

                    const items: FeatureType[] = reorder(
                      value,
                      result.source.index,
                      result.destination.index
                    );

                    onChange(items);
                  }}
                >
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver)}
                      >
                        {value.map((item: FeatureType, index: number) => (
                          <Draggable
                            key={index}
                            draggableId={`feature-${index}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                              >
                                <div className="relative w-32 h-20 mr-4 flex-shrink-0 flex-grow-0">
                                  <img
                                    src={imagePath(item.image) as string}
                                    width={500}
                                    height={375}
                                    alt={item.title}
                                  />
                                </div>
                                <div className="w-full pr-10">
                                  <h3 className="font-medium">{item.title}</h3>
                                  <p className="text-gray-450 font-light">
                                    {item.description}
                                  </p>
                                </div>
                                <div className="flex-grow-0 grid grid-rows-3 gap-2">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="w-7 h-7 bg-gray-500 flex items-center justify-center hover:bg-gray-550"
                                  >
                                    <TbHandMove />
                                  </div>
                                  <div
                                    role="button"
                                    className="w-7 h-7 bg-white text-black flex items-center justify-center"
                                  >
                                    <TbEdit />
                                  </div>
                                  <div
                                    role="button"
                                    className="w-7 h-7 bg-red-500 flex items-center justify-center"
                                  >
                                    <TbTrash />
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            />

            <FormFieldErrors
              errors={errors}
              field="page_template_config.features"
            />
          </div>
        </>
      )}
    />
  );
};

export default ContentPageEditFeatures;
